import React, { useMemo, useState } from 'react';
import {
  FaBullhorn,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboard,
  FaEnvelope,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaPaperPlane,
  FaPlusCircle,
  FaQuestionCircle,
  FaShieldAlt,
  FaTasks,
} from 'react-icons/fa';
import './ForfyCommerceCockpit.css';
import { FORFY_COMMERCE_AI_CONTRACT_VERSION, invokeForfyCommerceAi, type ForfyCommerceAiResponse } from '../../lib/forfyCommerceAiClient';

const offerCategories = [
  'Expérience',
  'Hébergement',
  'Restaurant',
  'Spa',
  'Activité',
  'Bundle local',
] as const;

type OfferCategory = (typeof offerCategories)[number];

type OfferDraft = {
  category: OfferCategory;
  title: string;
  promise: string;
  inclusions: string;
  limits: string;
  conditions: string;
  media: string;
};

type ReadinessCheck = {
  id: string;
  label: string;
  detail: string;
  done: boolean;
};

type MarketingCopyItem = {
  channel: string;
  title: string;
  body: string;
  nextStep: string;
};

type LaunchPlanItem = {
  day: string;
  title: string;
  action: string;
  owner: string;
  channel: string;
};

type OfferHandoffPacket = {
  schemaVersion: 'forfy-offer-handoff-v22n9';
  source: 'ForfyCommerceOfferAssistant';
  title: string;
  category: OfferCategory;
  readinessScore: number;
  readinessLabel: string;
  offerSheet: string;
  marketingPackText: string;
  launchPlanText: string;
  guardrails: string[];
};

type OfferFollowUpTask = {
  id: string;
  title: string;
  owner: string;
  priority: 'Haute' | 'Moyenne' | 'Suivi';
  due: string;
  action: string;
};

type CustomerFaqItem = {
  id: string;
  question: string;
  answer: string;
  channel: string;
  risk: 'Faible' | 'Moyen' | 'À valider';
};

type ForfyAiComposerStatus = 'idle' | 'loading' | 'ready' | 'blocked';

type ForfyAiDossierActionStatus = 'idle' | 'applied' | 'prepared' | 'copied';

type ForfyCommerceOfferAssistantProps = {
  onOpenOffers?: () => void;
  onSendToAssistant?: (prompt: string) => void;
};

const defaultOfferDraft: OfferDraft = {
  category: 'Expérience',
  title: 'Escapade locale prête à réserver',
  promise: 'Offrir une expérience simple, mémorable et facile à acheter.',
  inclusions: 'Accueil personnalisé\nExpérience principale\nSuggestion de moment idéal',
  limits: 'Disponibilités limitées\nValidation manuelle avant publication',
  conditions: 'Prix officiel à confirmer dans Giftshop\nDisponibilités à confirmer dans Reserve',
  media: 'Photo principale\nPhoto ambiance\nCourte vidéo verticale',
};

const listFromText = (value: string, fallback: string) => {
  const items = value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

  return items.length ? items : [fallback];
};

const countUsefulItems = (value: string) =>
  listFromText(value, '').filter((item) => item.length >= 3).length;

const buildReadinessChecks = (draft: OfferDraft): ReadinessCheck[] => {
  const titleReady = draft.title.trim().length >= 12;
  const promiseReady = draft.promise.trim().length >= 24;
  const inclusionsReady = countUsefulItems(draft.inclusions) >= 2;
  const limitsReady = countUsefulItems(draft.limits) >= 1;
  const conditionsReady = countUsefulItems(draft.conditions) >= 2;
  const mediaReady = countUsefulItems(draft.media) >= 2;
  const authorityReady =
    draft.conditions.toLowerCase().includes('giftshop') ||
    draft.conditions.toLowerCase().includes('reserve') ||
    draft.conditions.toLowerCase().includes('prix');

  return [
    {
      id: 'title',
      label: 'Titre compréhensible',
      detail: titleReady ? 'Le titre est assez clair pour une fiche de travail.' : 'Ajoute un titre plus précis et vendeur.',
      done: titleReady,
    },
    {
      id: 'promise',
      label: 'Promesse client',
      detail: promiseReady ? 'La valeur client est exprimée.' : 'Explique le bénéfice client en une phrase complète.',
      done: promiseReady,
    },
    {
      id: 'inclusions',
      label: 'Inclusions structurées',
      detail: inclusionsReady ? 'Les inclusions donnent de la matière à l’offre.' : 'Ajoute au moins deux inclusions concrètes.',
      done: inclusionsReady,
    },
    {
      id: 'limits',
      label: 'Limites / exclusions',
      detail: limitsReady ? 'Les limites sont visibles avant publication.' : 'Ajoute les restrictions, exclusions ou disponibilités.',
      done: limitsReady,
    },
    {
      id: 'conditions',
      label: 'Conditions à confirmer',
      detail: conditionsReady ? 'Les validations opérationnelles sont listées.' : 'Ajoute prix, taxes, disponibilité ou politique à confirmer.',
      done: conditionsReady,
    },
    {
      id: 'media',
      label: 'Médias prévus',
      detail: mediaReady ? 'Les besoins médias sont prêts à transmettre.' : 'Ajoute les photos, vidéos ou visuels à prévoir.',
      done: mediaReady,
    },
    {
      id: 'authority',
      label: 'Autorités respectées',
      detail: authorityReady
        ? 'Giftshop / Reserve / validation humaine sont rappelés.'
        : 'Rappelle que Giftshop confirme le prix et Reserve les disponibilités.',
      done: authorityReady,
    },
  ];
};

const getReadinessLabel = (score: number) => {
  if (score >= 86) return 'Prête à valider';
  if (score >= 60) return 'Presque prête';
  return 'À compléter';
};

const buildMarketingCopyPack = (draft: OfferDraft, readinessScore: number): MarketingCopyItem[] => {
  const inclusions = listFromText(draft.inclusions, 'une expérience locale à préciser').slice(0, 3);
  const firstInclusion = inclusions[0] || 'une expérience locale';
  const shortTitle = draft.title.trim() || 'Nouvelle expérience Forfeo';
  const promise = draft.promise.trim() || 'Une expérience locale simple à offrir et facile à réserver.';

  return [
    {
      channel: 'Facebook / Instagram',
      title: `${shortTitle} — à découvrir`,
      body: `Envie de vivre quelque chose de simple, local et mémorable? ${promise}\n\nInclus: ${inclusions.join(' • ')}.\n\nOffre préparée avec Forfeo. Disponibilités et conditions à confirmer avant achat.`,
      nextStep: `Ajouter une photo principale, une image ambiance et un appel à réserver. Score interne: ${readinessScore}%.`,
    },
    {
      channel: 'TikTok / Reels',
      title: `Script court — ${draft.category}`,
      body: `Plan 1: montre l’ambiance.\nPlan 2: montre ${firstInclusion.toLowerCase()}.\nPlan 3: affiche la promesse: ${promise}\nTexte écran: "${shortTitle}"\nVoix: "Une idée locale simple à offrir ou à réserver avec Forfeo."`,
      nextStep: 'Filmer vertical, 12 à 20 secondes, avec sous-titres.',
    },
    {
      channel: 'LinkedIn',
      title: `Post partenaire — ${shortTitle}`,
      body: `Nous préparons une nouvelle offre locale avec Forfeo: ${shortTitle}.\n\nObjectif: rendre l’expérience plus claire, plus simple à découvrir et plus facile à réserver.\n\nPromesse: ${promise}\n\nValidation finale, prix et disponibilités restent confirmés avant publication.`,
      nextStep: 'Ajouter le contexte entreprise et une photo professionnelle.',
    },
    {
      channel: 'Courriel',
      title: `Sujet: Une nouvelle expérience locale à découvrir`,
      body: `Bonjour,\n\nNous préparons une offre ${draft.category.toLowerCase()} pensée pour créer un moment simple, agréable et mémorable.\n\n${shortTitle}\n${promise}\n\nÀ inclure: ${inclusions.join(', ')}.\n\nLes conditions finales, disponibilités et prix seront confirmés avant publication.\n\nL’équipe Forfeo`,
      nextStep: 'Valider le prix, ajouter le lien officiel et relire avant envoi.',
    },
    {
      channel: 'SMS / Messenger',
      title: `Message court`,
      body: `${shortTitle} — ${promise} Offre locale préparée avec Forfeo. Disponibilités et conditions à confirmer avant achat.`,
      nextStep: 'Raccourcir au besoin et ajouter le lien officiel seulement après validation.',
    },
  ];
};

const buildLaunchPlanner = (
  draft: OfferDraft,
  marketingCopyPack: MarketingCopyItem[],
  readinessScore: number,
): LaunchPlanItem[] => {
  const shortTitle = draft.title.trim() || 'Nouvelle expérience Forfeo';
  const primarySocial = marketingCopyPack.find((item) => item.channel === 'Facebook / Instagram');
  const shortVideo = marketingCopyPack.find((item) => item.channel === 'TikTok / Reels');
  const email = marketingCopyPack.find((item) => item.channel === 'Courriel');

  return [
    {
      day: 'J-7',
      title: 'Valider la fiche offre',
      action: `Relire "${shortTitle}", corriger les champs faibles et viser un score supérieur à 86%. Score actuel: ${readinessScore}%.`,
      owner: 'Commerçant + Forfeo',
      channel: 'Offre assistée',
    },
    {
      day: 'J-5',
      title: 'Confirmer prix et disponibilité',
      action: 'Confirmer le prix officiel côté Giftshop et les disponibilités côté Reserve avant toute promotion.',
      owner: 'Commerçant',
      channel: 'Giftshop / Reserve',
    },
    {
      day: 'J-3',
      title: 'Préparer les visuels',
      action: `Préparer les médias: ${draft.media || 'photo principale, photo ambiance et courte vidéo verticale'}.`,
      owner: 'Commerçant',
      channel: 'Médias',
    },
    {
      day: 'J-2',
      title: 'Préparer les textes sociaux',
      action: primarySocial?.body || 'Préparer le texte Facebook / Instagram et le faire valider.',
      owner: 'Marketing',
      channel: 'Facebook / Instagram',
    },
    {
      day: 'J-1',
      title: 'Préparer la courte vidéo',
      action: shortVideo?.body || 'Préparer un script vidéo court de 12 à 20 secondes.',
      owner: 'Marketing',
      channel: 'TikTok / Reels',
    },
    {
      day: 'Jour J',
      title: 'Lancement manuel',
      action: 'Publier seulement après validation humaine finale. Vérifier lien officiel, conditions, prix et disponibilité.',
      owner: 'Commerçant + Forfeo',
      channel: 'Publication manuelle',
    },
    {
      day: 'J+1',
      title: 'Suivi et optimisation',
      action: email?.body || 'Préparer une relance courriel ou message court selon les réactions.',
      owner: 'Commerçant',
      channel: 'Courriel / SMS',
    },
  ];
};

const buildOfferSheet = (draft: OfferDraft, checks: ReadinessCheck[], readinessScore: number) => {
  const inclusions = listFromText(draft.inclusions, 'Inclusions à préciser');
  const limits = listFromText(draft.limits, 'Limites à préciser');
  const conditions = listFromText(draft.conditions, 'Conditions à confirmer');
  const media = listFromText(draft.media, 'Médias à prévoir');
  const missingChecks = checks.filter((check) => !check.done);

  return [
    `FICHE OFFRE ASSISTÉE FORFY`,
    ``,
    `Score préparation: ${readinessScore}% — ${getReadinessLabel(readinessScore)}`,
    `Type d’offre: ${draft.category}`,
    `Titre de travail: ${draft.title}`,
    `Promesse client: ${draft.promise}`,
    ``,
    `Inclusions:`,
    ...inclusions.map((item, index) => `${index + 1}. ${item}`),
    ``,
    `Limites / exclusions:`,
    ...limits.map((item, index) => `${index + 1}. ${item}`),
    ``,
    `Conditions à confirmer avant publication:`,
    ...conditions.map((item, index) => `${index + 1}. ${item}`),
    ``,
    `Médias à prévoir:`,
    ...media.map((item, index) => `${index + 1}. ${item}`),
    ``,
    `Checklist avant publication:`,
    ...checks.map((check) => `${check.done ? '✓' : '•'} ${check.label} — ${check.detail}`),
    ``,
    `À régler avant publication:`,
    ...(missingChecks.length
      ? missingChecks.map((check, index) => `${index + 1}. ${check.detail}`)
      : ['1. Faire une dernière validation humaine avant publication.']),
    ``,
    `Garde-fous: Forfy prépare une fiche de travail seulement. Aucun prix officiel, aucun paiement, aucun remboursement, aucune commission, aucun payout, aucune taxe, aucune publication et aucune référence Stripe ne sont modifiés. Giftshop demeure l’autorité financière et transactionnelle. Reserve demeure l’autorité de disponibilité lorsque requis.`,
  ].join('\n');
};

const buildMarketingPackText = (items: MarketingCopyItem[]) =>
  [
    'PACK MARKETING FORFY — TEXTES À VALIDER AVANT PUBLICATION',
    '',
    ...items.flatMap((item, index) => [
      `${index + 1}. ${item.channel}`,
      `Titre: ${item.title}`,
      item.body,
      `Prochaine étape: ${item.nextStep}`,
      '',
    ]),
    'Garde-fous: ces textes sont des brouillons. Tous les textes doivent être validés avant utilisation. Aucune publication automatique, aucun prix officiel, aucun paiement, aucun remboursement, aucune commission, aucun payout et aucune donnée Giftshop ne sont modifiés.',
  ].join('\n');

const buildLaunchPlanText = (items: LaunchPlanItem[]) =>
  [
    'PLAN DE LANCEMENT FORFY — À VALIDER HUMAINEMENT',
    '',
    ...items.flatMap((item) => [
      `${item.day} — ${item.title}`,
      `Canal: ${item.channel}`,
      `Responsable: ${item.owner}`,
      `Action: ${item.action}`,
      '',
    ]),
    'Garde-fous: ce plan est un calendrier de travail. Aucune publication automatique, aucun prix officiel, aucun paiement, aucun remboursement, aucune commission, aucun payout, aucune taxe et aucune donnée Giftshop ne sont modifiés.',
  ].join('\n');

const buildOfferHandoffPacket = (
  draft: OfferDraft,
  readinessScore: number,
  offerSheet: string,
  marketingPackText: string,
  launchPlanText: string,
): OfferHandoffPacket => ({
  schemaVersion: 'forfy-offer-handoff-v22n9',
  source: 'ForfyCommerceOfferAssistant',
  title: draft.title.trim() || 'Nouvelle offre Forfeo',
  category: draft.category,
  readinessScore,
  readinessLabel: getReadinessLabel(readinessScore),
  offerSheet,
  marketingPackText,
  launchPlanText,
  guardrails: [
    'Validation humaine requise avant publication',
    'Giftshop demeure l’autorité financière et transactionnelle',
    'Reserve demeure l’autorité de disponibilité lorsque requis',
    'Aucune publication automatique depuis Forfy',
    'Aucun prix officiel modifié depuis Explore',
  ],
});

const buildOfferHandoffText = (packet: OfferHandoffPacket) =>
  [
    'DOSSIER OFFRE FORFY — BROUILLON À TRANSFÉRER VERS OFFRES',
    '',
    `Version: ${packet.schemaVersion}`,
    `Titre: ${packet.title}`,
    `Type: ${packet.category}`,
    `Score: ${packet.readinessScore}% — ${packet.readinessLabel}`,
    '',
    'GARDE-FOUS',
    ...packet.guardrails.map((item, index) => `${index + 1}. ${item}`),
    '',
    '--- FICHE OFFRE ---',
    packet.offerSheet,
    '',
    '--- PACK MARKETING ---',
    packet.marketingPackText,
    '',
    '--- PLAN DE LANCEMENT ---',
    packet.launchPlanText,
    '',
    'Statut: brouillon local à valider. Aucun paiement, aucune publication, aucun prix, aucune taxe, aucune commission, aucun payout et aucune donnée Giftshop ne sont modifiés.',
  ].join('\n');


const buildOfferFollowUpTasks = (draft: OfferDraft, readinessScore: number): OfferFollowUpTask[] => {
  const title = draft.title.trim() || 'Nouvelle offre Forfeo';
  const weakScore = readinessScore < 86;

  return [
    {
      id: 'validate-offer',
      title: 'Valider la fiche offre',
      owner: 'Commerçant',
      priority: weakScore ? 'Haute' : 'Moyenne',
      due: 'Avant publication',
      action: weakScore
        ? `Améliorer "${title}" jusqu’à un score supérieur à 86% avant de publier.`
        : `Relire "${title}" et confirmer que les inclusions, limites et conditions sont exactes.`,
    },
    {
      id: 'confirm-giftshop',
      title: 'Confirmer prix officiel',
      owner: 'Giftshop / Forfeo',
      priority: 'Haute',
      due: 'Avant mise en vente',
      action: 'Valider le prix, les taxes, la politique et la logique transactionnelle dans Giftshop.',
    },
    {
      id: 'confirm-reserve',
      title: 'Confirmer disponibilité',
      owner: 'Reserve / Commerçant',
      priority: 'Haute',
      due: 'Avant promotion',
      action: 'Valider les disponibilités, capacités, horaires ou fenêtres de réservation dans Reserve lorsque requis.',
    },
    {
      id: 'prepare-media',
      title: 'Préparer les médias',
      owner: 'Marketing',
      priority: 'Moyenne',
      due: 'J-3',
      action: `Préparer ou valider les médias: ${draft.media || 'photo principale, photo ambiance et courte vidéo verticale'}.`,
    },
    {
      id: 'launch-copy',
      title: 'Préparer les textes',
      owner: 'Marketing',
      priority: 'Moyenne',
      due: 'J-2',
      action: 'Adapter les textes Facebook, Instagram, TikTok, LinkedIn, courriel et SMS avant publication manuelle.',
    },
    {
      id: 'post-launch',
      title: 'Suivre les réactions',
      owner: 'Commerçant',
      priority: 'Suivi',
      due: 'J+1',
      action: 'Relire les questions clients, noter les objections et préparer une amélioration de l’offre.',
    },
  ];
};

const buildOfferFollowUpText = (tasks: OfferFollowUpTask[]) =>
  [
    'TÂCHES DE SUIVI FORFY — OFFRE COMMERÇANT',
    '',
    ...tasks.flatMap((task, index) => [
      `${index + 1}. ${task.title}`,
      `Responsable: ${task.owner}`,
      `Priorité: ${task.priority}`,
      `Échéance: ${task.due}`,
      `Action: ${task.action}`,
      '',
    ]),
    'Garde-fous: cette liste est opérationnelle seulement. Aucune publication automatique, aucun prix officiel, aucun paiement, aucun remboursement, aucune commission, aucun payout, aucune taxe et aucune donnée Giftshop ne sont modifiés.',
  ].join('\n');

const buildFollowUpAssistantPrompt = (draft: OfferDraft, followUpText: string) =>
  [
    'Agis comme Forfy Commerce, assistant opérationnel marchand.',
    '',
    'MISSION',
    'Transforme cette liste en plan de travail simple pour finaliser une offre Forfeo.',
    '',
    'CONSIGNES',
    '1. Classe les tâches par urgence.',
    '2. Garde la séparation Giftshop, Reserve, Forfeo et commerçant.',
    '3. Ne donne aucune autorité financière à Explore ou Forfy.',
    '4. Termine avec une mini-checklist que le commerçant peut suivre.',
    '',
    `Type d’offre: ${draft.category}`,
    `Titre de travail: ${draft.title}`,
    '',
    followUpText,
  ].join('\n');


const buildCustomerFaqPack = (draft: OfferDraft, readinessScore: number): CustomerFaqItem[] => {
  const title = draft.title.trim() || 'Nouvelle offre Forfeo';
  const inclusions = listFromText(draft.inclusions, 'les inclusions seront confirmées avant publication').slice(0, 3);
  const limits = listFromText(draft.limits, 'les limites seront précisées avant achat').slice(0, 2);
  const conditions = listFromText(draft.conditions, 'les conditions seront confirmées avant publication').slice(0, 2);
  const promise = draft.promise.trim() || 'une expérience locale simple, claire et mémorable';

  return [
    {
      id: 'included',
      question: 'Qu’est-ce qui est inclus?',
      answer: `Cette offre ${draft.category.toLowerCase()} est préparée autour de: ${inclusions.join(', ')}. Les inclusions finales devront être confirmées sur la fiche officielle avant achat.`,
      channel: 'Client',
      risk: 'Faible',
    },
    {
      id: 'reservation',
      question: 'Comment réserver?',
      answer: `La réservation se fera à partir du lien officiel Forfeo lorsque l’offre sera publiée. Les disponibilités doivent être confirmées avant toute promesse au client.`,
      channel: 'Réservation',
      risk: 'Moyen',
    },
    {
      id: 'price',
      question: 'Le prix est-il confirmé?',
      answer: `Pas encore dans cette fiche de travail. Giftshop reste l’autorité financière pour le prix officiel, les taxes, les paiements et les confirmations transactionnelles.`,
      channel: 'Prix',
      risk: 'À valider',
    },
    {
      id: 'gift',
      question: 'Puis-je l’offrir en cadeau?',
      answer: `Oui, l’offre peut être présentée comme une idée cadeau si elle est compatible avec les règles finales du marchand et de Forfeo. Le message client doit rester simple: ${promise}.`,
      channel: 'Cadeau',
      risk: 'Moyen',
    },
    {
      id: 'limits',
      question: 'Quelles sont les limites?',
      answer: `Les limites à communiquer avant achat: ${limits.join(', ')}. Conditions à confirmer: ${conditions.join(', ')}.`,
      channel: 'Conditions',
      risk: 'À valider',
    },
    {
      id: 'objection',
      question: 'Pourquoi acheter maintenant?',
      answer: `Parce que "${title}" transforme une intention en expérience locale concrète. Réponse prudente: le client doit acheter seulement après avoir lu les conditions, disponibilités et détails officiels.`,
      channel: 'Objection',
      risk: readinessScore >= 86 ? 'Faible' : 'Moyen',
    },
  ];
};

const buildCustomerFaqText = (items: CustomerFaqItem[]) =>
  [
    'QUESTIONS RÉPONSES CLIENTS FORFY — À VALIDER AVANT UTILISATION',
    '',
    ...items.flatMap((item, index) => [
      `${index + 1}. ${item.question}`,
      `Canal: ${item.channel}`,
      `Risque: ${item.risk}`,
      `Réponse: ${item.answer}`,
      '',
    ]),
    'Garde-fous: ces réponses sont des brouillons client. Ne promets aucun prix officiel, aucune disponibilité garantie, aucun remboursement, aucune commission, aucun payout, aucune taxe et aucune publication automatique. Giftshop reste l’autorité financière. Reserve reste l’autorité de disponibilité lorsque requis.',
  ].join('\n');

const buildCustomerFaqAssistantPrompt = (draft: OfferDraft, faqText: string) =>
  [
    'Agis comme Forfy Commerce, assistant expérience client marchand.',
    '',
    'MISSION',
    'Améliore ces questions/réponses pour aider le commerçant à répondre clairement aux clients.',
    '',
    'CONSIGNES',
    '1. Garde un ton humain, rassurant et professionnel.',
    '2. Ne promets aucun prix officiel ni disponibilité garantie.',
    '3. Rappelle les validations Giftshop et Reserve lorsque nécessaire.',
    '4. Donne une version courte pour SMS et une version plus complète pour courriel.',
    '',
    `Type d’offre: ${draft.category}`,
    `Titre de travail: ${draft.title}`,
    '',
    faqText,
  ].join('\n');


const buildForfyAiComposerText = (output: ForfyCommerceAiResponse | null) => {
  if (!output) {
    return 'Aucun dossier Forfy IA généré.';
  }

  return [
    'DOSSIER FORFY IA — COMMERCE',
    '',
    `Statut: ${output.status}`,
    `Titre: ${output.title}`,
    `Type: ${output.category}`,
    `Score: ${output.readinessScore}% — ${output.readinessLabel}`,
    '',
    'PROMESSE CLIENT',
    output.customerPromise,
    '',
    'CHAMPS À COMPLÉTER',
    ...(output.missingFields.length ? output.missingFields.map((field) => `- ${field}`) : ['- Aucun champ critique détecté']),
    '',
    'FICHE OFFRE',
    output.offerSheet,
    '',
    'MARKETING',
    `Facebook / Instagram: ${output.marketingPack.facebookInstagram}`,
    `TikTok / Reels: ${output.marketingPack.tiktokReels}`,
    `LinkedIn: ${output.marketingPack.linkedin}`,
    `Courriel: ${output.marketingPack.email}`,
    `SMS / Messenger: ${output.marketingPack.smsMessenger}`,
    '',
    'PLAN DE LANCEMENT',
    ...output.launchPlan.flatMap((item, index) => [
      `${index + 1}. ${item.day} — ${item.title}`,
      `Canal: ${item.channel}`,
      `Responsable: ${item.owner}`,
      `Action: ${item.action}`,
      '',
    ]),
    'TÂCHES',
    ...output.followUpTasks.flatMap((task, index) => [
      `${index + 1}. ${task.title}`,
      `Responsable: ${task.owner}`,
      `Priorité: ${task.priority}`,
      `Échéance: ${task.due}`,
      `Action: ${task.action}`,
      '',
    ]),
    'FAQ CLIENTS',
    ...output.customerFaq.flatMap((item, index) => [
      `${index + 1}. ${item.question}`,
      `Risque: ${item.risk}`,
      `Réponse: ${item.answer}`,
      '',
    ]),
    'GARDE-FOUS',
    ...output.guardrails.map((guardrail) => `- ${guardrail}`),
    '',
    'Validation humaine obligatoire. Giftshop demeure l’autorité financière. Reserve demeure l’autorité de disponibilité lorsque requis.',
  ].join('\n');
};

const buildForfyAiComposerAssistantPrompt = (draft: OfferDraft, output: ForfyCommerceAiResponse | null) =>
  [
    'Agis comme Forfy Commerce, assistant IA marchand.',
    '',
    'MISSION',
    'Transforme ce dossier Forfy IA en plan marchand simple, clair et prêt à valider.',
    '',
    'CONSIGNES',
    '1. Ne publie rien automatiquement.',
    '2. Ne crée aucun prix officiel.',
    '3. Ne promets aucune disponibilité garantie.',
    '4. Garde Giftshop comme autorité financière.',
    '5. Garde Reserve comme autorité de disponibilité lorsque requis.',
    '',
    `Titre actuel: ${draft.title || 'Nouvelle offre Forfeo'}`,
    `Type actuel: ${draft.category}`,
    '',
    buildForfyAiComposerText(output),
  ].join('\n');


const FORFY_AI_DOSSIER_STORAGE_KEY = 'forfy-commerce-offer-handoff-draft';

const buildForfyAiDossierHandoffPacket = (draft: OfferDraft, output: ForfyCommerceAiResponse) => ({
  source: 'forfy-ai-dossier-actions-v22o5',
  status: 'draft',
  preparedAt: new Date().toISOString(),
  title: output.title || draft.title || 'Nouvelle offre Forfeo',
  category: output.category || draft.category,
  customerPromise: output.customerPromise || draft.promise,
  offerSheet: output.offerSheet,
  readinessScore: output.readinessScore,
  readinessLabel: output.readinessLabel,
  missingFields: output.missingFields,
  marketingPack: output.marketingPack,
  launchPlan: output.launchPlan,
  followUpTasks: output.followUpTasks,
  customerFaq: output.customerFaq,
  guardrails: output.guardrails,
  draft: {
    title: draft.title,
    category: draft.category,
    promise: draft.promise,
    inclusions: draft.inclusions,
    limits: draft.limits,
    conditions: draft.conditions,
    media: draft.media,
  },
});

const buildAssistantPrompt = (draft: OfferDraft, sheet: string, readinessScore: number) => {
  return [
    `Agis comme Forfy Commerce, assistant marchand de Forfeo.`,
    ``,
    `MISSION`,
    `Aide le commerçant à transformer cette fiche en offre claire, vendable et prête à valider humainement.`,
    ``,
    `CONTEXTE OFFRE`,
    `- Type: ${draft.category}`,
    `- Titre de travail: ${draft.title}`,
    `- Promesse client: ${draft.promise}`,
    `- Score préparation: ${readinessScore}%`,
    ``,
    `SORTIE ATTENDUE`,
    `1. Reformule le titre pour qu’il soit plus vendeur.`,
    `2. Améliore la promesse client.`,
    `3. Structure les inclusions et limites.`,
    `4. Liste les validations humaines à faire avant publication.`,
    `5. Propose 3 angles marketing courts.`,
    `6. Rappelle que la publication, le prix, les disponibilités et la validation finale restent manuels ou côté système autorisé.`,
    ``,
    sheet,
  ].join('\n');
};

const buildMarketingAssistantPrompt = (draft: OfferDraft, marketingPackText: string) =>
  [
    'Agis comme Forfy Commerce, assistant marketing marchand.',
    '',
    'MISSION',
    'Relis et améliore ce pack marketing multicanal pour une offre Forfeo.',
    '',
    'CONSIGNES',
    '1. Rends les textes plus clairs, plus premium et plus humains.',
    '2. Garde un ton local, crédible et non exagéré.',
    '3. Ne promets aucun prix, aucune disponibilité et aucune publication automatique.',
    '4. Prépare les textes pour validation humaine.',
    '',
    `Type d’offre: ${draft.category}`,
    `Titre de travail: ${draft.title}`,
    '',
    marketingPackText,
  ].join('\n');

const buildLaunchAssistantPrompt = (draft: OfferDraft, launchPlanText: string) =>
  [
    'Agis comme Forfy Commerce, assistant de lancement marchand.',
    '',
    'MISSION',
    'Transforme ce plan en plan d’action clair pour lancer une offre Forfeo sans automatiser la publication.',
    '',
    'CONSIGNES',
    '1. Priorise les actions humaines.',
    '2. Mets en évidence ce qui doit être validé avant publication.',
    '3. Rappelle que Giftshop reste l’autorité financière et Reserve l’autorité de disponibilité.',
    '4. Propose une version courte que le commerçant peut partager à son équipe.',
    '',
    `Type d’offre: ${draft.category}`,
    `Titre de travail: ${draft.title}`,
    '',
    launchPlanText,
  ].join('\n');

const copyText = async (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') {
    return;
  }

  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', 'true');
  area.style.position = 'fixed';
  area.style.left = '-9999px';
  document.body.appendChild(area);
  area.select();

  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(area);
  }
};

const ForfyCommerceOfferAssistant = ({
  onOpenOffers,
  onSendToAssistant,
}: ForfyCommerceOfferAssistantProps) => {
  const [draft, setDraft] = useState<OfferDraft>(defaultOfferDraft);
  const [copied, setCopied] = useState(false);
  const [copiedPack, setCopiedPack] = useState(false);
  const [copiedLaunchPlan, setCopiedLaunchPlan] = useState(false);
  const [copiedHandoff, setCopiedHandoff] = useState(false);
  const [handoffStatus, setHandoffStatus] = useState('Brouillon non transféré');
  const [copiedFollowUpTasks, setCopiedFollowUpTasks] = useState(false);
  const [copiedCustomerFaq, setCopiedCustomerFaq] = useState(false);
  const [forfyAiComposerStatus, setForfyAiComposerStatus] = useState<ForfyAiComposerStatus>('idle');
  const [forfyAiComposerMessage, setForfyAiComposerMessage] = useState('Forfy IA est prêt à composer un dossier marchand.');
  const [forfyAiComposerOutput, setForfyAiComposerOutput] = useState<ForfyCommerceAiResponse | null>(null);
  const [copiedForfyAiComposer, setCopiedForfyAiComposer] = useState(false);
  const [forfyAiQuickIdea, setForfyAiQuickIdea] = useState('');
  const [forfyAiDossierActionStatus, setForfyAiDossierActionStatus] = useState<ForfyAiDossierActionStatus>('idle');

  const readinessChecks = useMemo(() => buildReadinessChecks(draft), [draft]);
  const readinessScore = useMemo(() => {
    const done = readinessChecks.filter((check) => check.done).length;
    return Math.round((done / readinessChecks.length) * 100);
  }, [readinessChecks]);

  const offerSheet = useMemo(
    () => buildOfferSheet(draft, readinessChecks, readinessScore),
    [draft, readinessChecks, readinessScore],
  );

  const assistantPrompt = useMemo(
    () => buildAssistantPrompt(draft, offerSheet, readinessScore),
    [draft, offerSheet, readinessScore],
  );

  const marketingCopyPack = useMemo(
    () => buildMarketingCopyPack(draft, readinessScore),
    [draft, readinessScore],
  );

  const marketingPackText = useMemo(
    () => buildMarketingPackText(marketingCopyPack),
    [marketingCopyPack],
  );

  const marketingAssistantPrompt = useMemo(
    () => buildMarketingAssistantPrompt(draft, marketingPackText),
    [draft, marketingPackText],
  );

  const launchPlan = useMemo(
    () => buildLaunchPlanner(draft, marketingCopyPack, readinessScore),
    [draft, marketingCopyPack, readinessScore],
  );

  const launchPlanText = useMemo(
    () => buildLaunchPlanText(launchPlan),
    [launchPlan],
  );

  const launchAssistantPrompt = useMemo(
    () => buildLaunchAssistantPrompt(draft, launchPlanText),
    [draft, launchPlanText],
  );

  const offerHandoffPacket = useMemo(
    () => buildOfferHandoffPacket(draft, readinessScore, offerSheet, marketingPackText, launchPlanText),
    [draft, readinessScore, offerSheet, marketingPackText, launchPlanText],
  );

  const offerHandoffText = useMemo(
    () => buildOfferHandoffText(offerHandoffPacket),
    [offerHandoffPacket],
  );

  const followUpTasks = useMemo(
    () => buildOfferFollowUpTasks(draft, readinessScore),
    [draft, readinessScore],
  );

  const followUpTasksText = useMemo(
    () => buildOfferFollowUpText(followUpTasks),
    [followUpTasks],
  );

  const followUpAssistantPrompt = useMemo(
    () => buildFollowUpAssistantPrompt(draft, followUpTasksText),
    [draft, followUpTasksText],
  );

  const customerFaqPack = useMemo(
    () => buildCustomerFaqPack(draft, readinessScore),
    [draft, readinessScore],
  );

  const customerFaqText = useMemo(
    () => buildCustomerFaqText(customerFaqPack),
    [customerFaqPack],
  );

  const customerFaqAssistantPrompt = useMemo(
    () => buildCustomerFaqAssistantPrompt(draft, customerFaqText),
    [draft, customerFaqText],
  );

  const forfyAiComposerText = useMemo(
    () => buildForfyAiComposerText(forfyAiComposerOutput),
    [forfyAiComposerOutput],
  );

  const forfyAiComposerAssistantPrompt = useMemo(
    () => buildForfyAiComposerAssistantPrompt(draft, forfyAiComposerOutput),
    [draft, forfyAiComposerOutput],
  );

  const updateDraft = <K extends keyof OfferDraft>(key: K, value: OfferDraft[K]) => {
    setDraft((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleCopy = async () => {
    await copyText(offerSheet);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleCopyMarketingPack = async () => {
    await copyText(marketingPackText);
    setCopiedPack(true);
    window.setTimeout(() => setCopiedPack(false), 1800);
  };

  const handleCopyLaunchPlan = async () => {
    await copyText(launchPlanText);
    setCopiedLaunchPlan(true);
    window.setTimeout(() => setCopiedLaunchPlan(false), 1800);
  };

  const handleSendToAssistant = () => {
    if (onSendToAssistant) {
      onSendToAssistant(assistantPrompt);
      return;
    }

    void handleCopy();
  };

  const handleSendMarketingPack = () => {
    if (onSendToAssistant) {
      onSendToAssistant(marketingAssistantPrompt);
      return;
    }

    void handleCopyMarketingPack();
  };

  const handleSendLaunchPlan = () => {
    if (onSendToAssistant) {
      onSendToAssistant(launchAssistantPrompt);
      return;
    }

    void handleCopyLaunchPlan();
  };

  const handleCopyHandoffPacket = async () => {
    await copyText(offerHandoffText);
    setCopiedHandoff(true);
    window.setTimeout(() => setCopiedHandoff(false), 1800);
  };

  const handlePrepareOfferHandoff = () => {
    const packet = {
      ...offerHandoffPacket,
      preparedAt: new Date().toISOString(),
    };

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('forfy-commerce-offer-handoff-draft', JSON.stringify(packet));
      }
    } catch (error) {
      console.warn('[Forfy Commerce] local offer handoff skipped', error);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('commerce:offer-draft-prepared', {
          detail: packet,
        }),
      );

      window.dispatchEvent(
        new CustomEvent('commerce:open-tab', {
          detail: {
            tab: 'offersHub',
            source: 'forfy-offer-handoff-v22n9',
            draft: packet,
          },
        }),
      );
    }

    setHandoffStatus('Dossier préparé pour l’onglet Offres');

    if (onOpenOffers) {
      onOpenOffers();
    }
  };

  const handleCopyFollowUpTasks = async () => {
    await copyText(followUpTasksText);
    setCopiedFollowUpTasks(true);
    window.setTimeout(() => setCopiedFollowUpTasks(false), 1800);
  };

  const handleSendFollowUpTasks = () => {
    if (onSendToAssistant) {
      onSendToAssistant(followUpAssistantPrompt);
      return;
    }

    void handleCopyFollowUpTasks();
  };

  const handleCopyCustomerFaq = async () => {
    await copyText(customerFaqText);
    setCopiedCustomerFaq(true);
    window.setTimeout(() => setCopiedCustomerFaq(false), 1800);
  };

  const handleSendCustomerFaq = () => {
    if (onSendToAssistant) {
      onSendToAssistant(customerFaqAssistantPrompt);
      return;
    }

    void handleCopyCustomerFaq();
  };

  const handleGenerateFromForfyAiIdea = async () => {
    const idea = forfyAiQuickIdea.trim();

    if (!idea) {
      setForfyAiComposerStatus('blocked');
      setForfyAiComposerMessage('Décris ton idée en une phrase avant de générer le dossier.');
      return;
    }

    setForfyAiComposerStatus('loading');
    setForfyAiComposerMessage('Forfy IA transforme ton idée en dossier marchand...');

    const output = await invokeForfyCommerceAi({
      contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
      intent: 'compose_offer',
      locale: 'fr-CA',
      merchantContext: {
        businessName: null,
        businessType: draft.category,
        region: 'Québec',
        plan: null,
      },
      offerIdea: {
        title: idea,
        category: draft.category,
        promise: idea,
        inclusions: [idea, ...listFromText(draft.inclusions, 'inclusion à préciser')].slice(0, 5),
        limits: listFromText(draft.limits, 'limite à préciser'),
        conditions: listFromText(draft.conditions, 'condition à préciser'),
        media: listFromText(draft.media, 'média à préciser'),
      },
    });

    setForfyAiComposerOutput(output);

    if (output.status === 'blocked') {
      setForfyAiComposerStatus('blocked');
      setForfyAiComposerMessage(output.customerPromise || 'Forfy IA est indisponible. Le brouillon local reste utilisable.');
      return;
    }

    setDraft((current) => ({
      ...current,
      title: output.title || current.title || idea,
      promise: output.customerPromise || current.promise || idea,
    }));

    setForfyAiComposerStatus('ready');
    setForfyAiComposerMessage('Dossier Forfy IA généré à partir de ton idée. Relis et valide avant publication.');
  };

  const handleComposeWithForfyAi = async () => {
    setForfyAiComposerStatus('loading');
    setForfyAiComposerMessage('Forfy IA compose le dossier marchand sécurisé...');

    const output = await invokeForfyCommerceAi({
      contractVersion: FORFY_COMMERCE_AI_CONTRACT_VERSION,
      intent: 'compose_offer',
      locale: 'fr-CA',
      merchantContext: {
        businessName: null,
        businessType: draft.category,
        region: 'Québec',
        plan: null,
      },
      offerIdea: {
        title: draft.title,
        category: draft.category,
        promise: draft.promise,
        inclusions: listFromText(draft.inclusions, 'inclusion à préciser'),
        limits: listFromText(draft.limits, 'limite à préciser'),
        conditions: listFromText(draft.conditions, 'condition à préciser'),
        media: listFromText(draft.media, 'média à préciser'),
      },
    });

    setForfyAiComposerOutput(output);

    if (output.status === 'blocked') {
      setForfyAiComposerStatus('blocked');
      setForfyAiComposerMessage(output.customerPromise || 'Forfy IA est indisponible. Le brouillon local reste utilisable.');
      return;
    }

    setDraft((current) => ({
      ...current,
      title: output.title || current.title,
      promise: output.customerPromise || current.promise,
    }));

    setForfyAiComposerStatus('ready');
    setForfyAiComposerMessage('Dossier Forfy IA généré. Relis et valide avant toute publication.');
  };

  const handleCopyForfyAiComposer = async () => {
    await copyText(forfyAiComposerText);
    setCopiedForfyAiComposer(true);
    window.setTimeout(() => setCopiedForfyAiComposer(false), 1800);
  };

  const handleSendForfyAiComposer = () => {
    if (onSendToAssistant) {
      onSendToAssistant(forfyAiComposerAssistantPrompt);
      return;
    }

    void handleCopyForfyAiComposer();
  };

  const handleApplyForfyAiDossier = () => {
    const output = forfyAiComposerOutput;

    if (!output) {
      setForfyAiComposerStatus('blocked');
      setForfyAiComposerMessage('Génère d’abord un dossier Forfy IA.');
      return;
    }

    setDraft((current) => ({
      ...current,
      title: output.title || current.title,
      promise: output.customerPromise || current.promise,
    }));

    setForfyAiDossierActionStatus('applied');
    setForfyAiComposerMessage('Titre et promesse appliqués au brouillon. Validation humaine requise.');
  };

  const handlePrepareForfyAiDossierInOffers = () => {
    const output = forfyAiComposerOutput;

    if (!output) {
      setForfyAiComposerStatus('blocked');
      setForfyAiComposerMessage('Génère d’abord un dossier Forfy IA.');
      return;
    }

    const packet = buildForfyAiDossierHandoffPacket(draft, output);

    try {
      window.localStorage.setItem(FORFY_AI_DOSSIER_STORAGE_KEY, JSON.stringify(packet));
    } catch {
      // Le flux reste utilisable même si le navigateur bloque le stockage local.
    }

    window.dispatchEvent(new CustomEvent('commerce:offer-draft-prepared', {
      detail: packet,
    }));

    window.dispatchEvent(new CustomEvent('commerce:open-tab', {
      detail: {
        tab: 'offersHub',
        source: 'forfy-ai-dossier-actions-v22o5',
      },
    }));

    setForfyAiDossierActionStatus('prepared');
    setForfyAiComposerMessage('Dossier préparé dans Offres. Relis et valide avant publication.');
  };

  const handleCopyForfyAiFullDossier = async () => {
    await copyText(forfyAiComposerText);
    setForfyAiDossierActionStatus('copied');
    window.setTimeout(() => setForfyAiDossierActionStatus('idle'), 1800);
  };

  const handleOpenOffers = () => {
    if (onOpenOffers) {
      onOpenOffers();
      return;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('commerce:open-tab', {
          detail: {
            tab: 'offersHub',
            source: 'forfy-offer-assistant-safe',
          },
        }),
      );
    }
  };

  return (
    <section
      id="forfy-offer-assistant"
      className="forfy-commerce-offer-assistant forfy-commerce-cockpit__section-card"
      data-forfy-offer-assistant="v22n5-readiness"
      aria-label="Assistant création d’offre Forfy Commerce"
    >
      <div className="forfy-commerce-offer-assistant__header">
        <div>
          <p className="forfy-commerce-cockpit__card-eyebrow">Offre assistée</p>
          <h3>Créer une fiche offre sans publier automatiquement</h3>
          <span>
            Forfy prépare une fiche claire. Le commerçant valide, puis l’onglet Offres reste le lieu de création réelle.
          </span>
        </div>

        <div className="forfy-commerce-offer-assistant__guardrail">
          <FaShieldAlt />
          <strong>Validation humaine requise</strong>
        </div>
      </div>

      <div className="forfy-commerce-offer-readiness" aria-label="Score de préparation offre">
        <div className="forfy-commerce-offer-readiness__score">
          <span>{readinessScore}%</span>
          <strong>{getReadinessLabel(readinessScore)}</strong>
          <small>Score de préparation avant publication</small>
        </div>

        <div className="forfy-commerce-offer-readiness__checks">
          {readinessChecks.map((check) => (
            <article
              key={check.id}
              className={`forfy-commerce-offer-readiness__check ${
                check.done ? 'forfy-commerce-offer-readiness__check--done' : ''
              }`}
            >
              {check.done ? <FaCheckCircle /> : <FaExclamationTriangle />}
              <div>
                <strong>{check.label}</strong>
                <span>{check.detail}</span>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className="forfy-commerce-offer-assistant__grid">
        <label>
          Type d’offre
          <select
            value={draft.category}
            onChange={(event) => updateDraft('category', event.target.value as OfferCategory)}
          >
            {offerCategories.map((category) => (
              <option value={category} key={category}>
                {category}
              </option>
            ))}
          </select>
        </label>

        <label>
          Titre de travail
          <input
            value={draft.title}
            onChange={(event) => updateDraft('title', event.target.value)}
            placeholder="Ex.: Week-end spa et resto local"
          />
        </label>

        <label className="forfy-commerce-offer-assistant__wide">
          Promesse client
          <textarea
            value={draft.promise}
            onChange={(event) => updateDraft('promise', event.target.value)}
            placeholder="Explique la valeur client en une phrase."
          />
        </label>

        <label>
          Inclusions
          <textarea
            value={draft.inclusions}
            onChange={(event) => updateDraft('inclusions', event.target.value)}
            placeholder="Une inclusion par ligne."
          />
        </label>

        <label>
          Limites / exclusions
          <textarea
            value={draft.limits}
            onChange={(event) => updateDraft('limits', event.target.value)}
            placeholder="Disponibilités, exclusions, restrictions."
          />
        </label>

        <label>
          Conditions à confirmer
          <textarea
            value={draft.conditions}
            onChange={(event) => updateDraft('conditions', event.target.value)}
            placeholder="Prix, taxes, disponibilités, politique."
          />
        </label>

        <label>
          Médias à prévoir
          <textarea
            value={draft.media}
            onChange={(event) => updateDraft('media', event.target.value)}
            placeholder="Photos, vidéos, logo, ambiance."
          />
        </label>
      </div>

      <article className="forfy-commerce-offer-assistant__sheet">
        <div>
          <p className="forfy-commerce-cockpit__card-eyebrow">Fiche générée</p>
          <h4>{draft.title}</h4>
        </div>

        <pre>{offerSheet}</pre>
      </article>

      <section
        className="forfy-commerce-marketing-copy-pack"
        data-forfy-marketing-copy-pack="v22n6"
        aria-label="Pack marketing Forfy Commerce"
      >
        <div className="forfy-commerce-marketing-copy-pack__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Pack marketing</p>
            <h4>Textes prêts à adapter pour promouvoir l’offre</h4>
            <span>Aucune publication automatique. Tous les textes doivent être validés avant utilisation.</span>
          </div>

          <div className="forfy-commerce-marketing-copy-pack__badge">
            <FaBullhorn />
            <strong>5 canaux</strong>
          </div>
        </div>

        <div className="forfy-commerce-marketing-copy-pack__grid">
          {marketingCopyPack.map((item) => (
            <article key={item.channel} className="forfy-commerce-marketing-copy-pack__card">
              <p>{item.channel}</p>
              <h5>{item.title}</h5>
              <pre>{item.body}</pre>
              <small>{item.nextStep}</small>
            </article>
          ))}
        </div>

        <div className="forfy-commerce-marketing-copy-pack__actions">
          <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyMarketingPack}>
            <FaClipboard /> {copiedPack ? 'Pack copié' : 'Copier pack marketing'}
          </button>

          <button type="button" className="forfy-commerce-cockpit__primary" onClick={handleSendMarketingPack}>
            <FaEnvelope /> Envoyer pack à l’assistant
          </button>
        </div>
      </section>

      <section
        className="forfy-commerce-launch-planner"
        data-forfy-launch-planner="v22n7"
        aria-label="Plan de lancement Forfy Commerce"
      >
        <div className="forfy-commerce-launch-planner__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Plan de lancement</p>
            <h4>Transformer l’offre en séquence prête à exécuter</h4>
            <span>Forfy organise les actions humaines de J-7 à J+1. Rien n’est publié automatiquement.</span>
          </div>

          <div className="forfy-commerce-launch-planner__badge">
            <FaCalendarAlt />
            <strong>J-7 à J+1</strong>
          </div>
        </div>

        <div className="forfy-commerce-launch-planner__timeline">
          {launchPlan.map((item) => (
            <article key={`${item.day}-${item.title}`} className="forfy-commerce-launch-planner__item">
              <div className="forfy-commerce-launch-planner__day">{item.day}</div>
              <div>
                <p>{item.channel}</p>
                <h5>{item.title}</h5>
                <span>{item.action}</span>
                <small>Responsable: {item.owner}</small>
              </div>
            </article>
          ))}
        </div>

        <div className="forfy-commerce-launch-planner__actions">
          <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyLaunchPlan}>
            <FaClipboard /> {copiedLaunchPlan ? 'Plan copié' : 'Copier plan de lancement'}
          </button>

          <button type="button" className="forfy-commerce-cockpit__primary" onClick={handleSendLaunchPlan}>
            <FaTasks /> Envoyer plan à l’assistant
          </button>
        </div>
      </section>

      <section
        className="forfy-commerce-offer-handoff"
        data-forfy-offer-handoff="v22n9"
        aria-label="Dossier de transfert vers Offres"
      >
        <div className="forfy-commerce-offer-handoff__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Transfert vers Offres</p>
            <h4>Dossier prêt à remettre dans l’onglet Offres</h4>
            <span>
              Forfy prépare le paquet de travail. L’offre réelle, le prix officiel et la publication restent à valider.
            </span>
          </div>

          <div className="forfy-commerce-offer-handoff__status">
            <strong>{readinessScore}%</strong>
            <small>{handoffStatus}</small>
          </div>
        </div>

        <div className="forfy-commerce-offer-handoff__summary">
          <article>
            <strong>Fiche offre</strong>
            <span>Titre, promesse, inclusions, limites et conditions.</span>
          </article>
          <article>
            <strong>Pack marketing</strong>
            <span>Facebook, Instagram, TikTok, LinkedIn, courriel et SMS.</span>
          </article>
          <article>
            <strong>Plan lancement</strong>
            <span>Séquence humaine de J-7 à J+1.</span>
          </article>
        </div>

        <div className="forfy-commerce-offer-handoff__guardrail">
          Validation humaine obligatoire. Aucun prix officiel, paiement, remboursement, commission,
          payout, taxe, publication automatique ou donnée Giftshop n’est modifié.
        </div>

        <div className="forfy-commerce-offer-handoff__actions">
          <button type="button" className="forfy-commerce-cockpit__primary" onClick={handlePrepareOfferHandoff}>
            <FaExternalLinkAlt /> Préparer dans Offres
          </button>

          <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyHandoffPacket}>
            <FaClipboard /> {copiedHandoff ? 'Dossier copié' : 'Copier dossier complet'}
          </button>
        </div>
      </section>

      <section
        className="forfy-commerce-follow-up-tasks"
        data-forfy-follow-up-tasks="v22n12"
        aria-label="Tâches de suivi Forfy Commerce"
      >
        <div className="forfy-commerce-follow-up-tasks__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Tâches de suivi</p>
            <h4>Ce qu’il reste à faire après la préparation de l’offre</h4>
            <span>
              Forfy transforme l’offre en plan de travail humain. Rien n’est publié, facturé ou modifié automatiquement.
            </span>
          </div>

          <div className="forfy-commerce-follow-up-tasks__badge">
            <FaTasks />
            <strong>{followUpTasks.length} tâches</strong>
          </div>
        </div>

        <div className="forfy-commerce-follow-up-tasks__grid">
          {followUpTasks.map((task) => (
            <article key={task.id} className="forfy-commerce-follow-up-tasks__card">
              <div>
                <p>{task.priority}</p>
                <h5>{task.title}</h5>
              </div>
              <span>{task.action}</span>
              <small>{task.owner} · {task.due}</small>
            </article>
          ))}
        </div>

        <div className="forfy-commerce-follow-up-tasks__guardrail">
          Liste de travail seulement. Validation humaine obligatoire avant prix, disponibilité, publication ou transaction.
        </div>

        <div className="forfy-commerce-follow-up-tasks__actions">
          <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyFollowUpTasks}>
            <FaClipboard /> {copiedFollowUpTasks ? 'Tâches copiées' : 'Copier tâches'}
          </button>

          <button type="button" className="forfy-commerce-cockpit__primary" onClick={handleSendFollowUpTasks}>
            <FaTasks /> Envoyer tâches à l’assistant
          </button>
        </div>
      </section>

      <section
        className="forfy-commerce-customer-faq"
        data-forfy-customer-faq="v22n13"
        aria-label="Questions réponses clients Forfy Commerce"
      >
        <div className="forfy-commerce-customer-faq__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Réponses clients</p>
            <h4>Questions fréquentes et objections à préparer</h4>
            <span>
              Forfy prépare des réponses prudentes pour les clients. Prix, disponibilités et conditions restent à valider.
            </span>
          </div>

          <div className="forfy-commerce-customer-faq__badge">
            <FaQuestionCircle />
            <strong>{customerFaqPack.length} réponses</strong>
          </div>
        </div>

        <div className="forfy-commerce-customer-faq__grid">
          {customerFaqPack.map((item) => (
            <article key={item.id} className="forfy-commerce-customer-faq__card">
              <div>
                <p>{item.channel} · {item.risk}</p>
                <h5>{item.question}</h5>
              </div>
              <span>{item.answer}</span>
            </article>
          ))}
        </div>

        <div className="forfy-commerce-customer-faq__guardrail">
          Brouillon client seulement. Ne promets aucun prix officiel, aucune disponibilité garantie,
          aucun remboursement et aucune publication automatique.
        </div>

        <div className="forfy-commerce-customer-faq__actions">
          <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyCustomerFaq}>
            <FaClipboard /> {copiedCustomerFaq ? 'Réponses copiées' : 'Copier réponses clients'}
          </button>

          <button type="button" className="forfy-commerce-cockpit__primary" onClick={handleSendCustomerFaq}>
            <FaQuestionCircle /> Envoyer réponses à l’assistant
          </button>
        </div>
      </section>

      <section
        className="forfy-commerce-ai-quickstart"
        data-forfy-ai-quickstart="v22o4"
        aria-label="Composer rapidement une offre avec Forfy IA"
      >
        <div className="forfy-commerce-ai-quickstart__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Forfy IA</p>
            <h4>Décris ton offre en une phrase</h4>
            <span>
              Écris l’idée simplement. Forfy IA prépare ensuite l’offre, le marketing, les tâches et les réponses clients.
            </span>
          </div>
        </div>

        <textarea
          className="forfy-commerce-ai-quickstart__textarea"
          value={forfyAiQuickIdea}
          onChange={(event) => setForfyAiQuickIdea(event.currentTarget.value)}
          placeholder="Ex. Week-end détente pour deux avec spa, souper trois services et départ tardif."
          rows={4}
        />

        <div className="forfy-commerce-ai-quickstart__actions">
          <button
            type="button"
            className="forfy-commerce-cockpit__primary"
            onClick={handleGenerateFromForfyAiIdea}
            disabled={forfyAiComposerStatus === 'loading'}
          >
            {forfyAiComposerStatus === 'loading' ? 'Forfy IA génère...' : 'Générer avec Forfy IA'}
          </button>

          <button
            type="button"
            className="forfy-commerce-cockpit__secondary"
            onClick={() => setForfyAiQuickIdea('Expérience premium pour deux personnes avec moment détente, repas local, attention personnalisée et disponibilité limitée.')}
          >
            Exemple premium
          </button>
        </div>

        <div className="forfy-commerce-ai-quickstart__guardrail">
          Forfy IA prépare un brouillon. L’humain valide toujours avant publication, prix officiel ou disponibilité.
        </div>
      </section>

      <section
        className="forfy-commerce-ai-composer"
        data-forfy-ai-composer="v22o3"
        aria-label="Composer une offre avec Forfy IA"
      >
        <div className="forfy-commerce-ai-composer__header">
          <div>
            <p className="forfy-commerce-cockpit__card-eyebrow">Forfy IA</p>
            <h4>Composer le dossier complet avec l’IA Forfy</h4>
            <span>
              Forfy IA transforme ton idée en offre, marketing, tâches et FAQ. Les secrets restent côté serveur.
            </span>
          </div>

          <div className={`forfy-commerce-ai-composer__status forfy-commerce-ai-composer__status--${forfyAiComposerStatus}`}>
            <strong>{forfyAiComposerStatus === 'loading' ? 'En cours' : forfyAiComposerStatus === 'ready' ? 'Prêt' : forfyAiComposerStatus === 'blocked' ? 'Bloqué' : 'Serveur'}</strong>
            <small>{forfyAiComposerMessage}</small>
          </div>
        </div>

        <div className="forfy-commerce-ai-composer__actions">
          <button
            type="button"
            className="forfy-commerce-cockpit__primary"
            onClick={handleComposeWithForfyAi}
            disabled={forfyAiComposerStatus === 'loading'}
          >
            {forfyAiComposerStatus === 'loading' ? 'Forfy IA compose...' : 'Composer avec Forfy IA'}
          </button>

          <button
            type="button"
            className="forfy-commerce-cockpit__secondary"
            onClick={handleCopyForfyAiComposer}
            disabled={!forfyAiComposerOutput}
          >
            <FaClipboard /> {copiedForfyAiComposer ? 'Dossier IA copié' : 'Copier dossier IA'}
          </button>

          <button
            type="button"
            className="forfy-commerce-cockpit__secondary"
            onClick={handleSendForfyAiComposer}
            disabled={!forfyAiComposerOutput}
          >
            Envoyer à Forfy
          </button>
        </div>

        {forfyAiComposerOutput && (
          <div className="forfy-commerce-ai-composer__result">
            <div>
              <strong>{forfyAiComposerOutput.title}</strong>
              <span>{forfyAiComposerOutput.category} · {forfyAiComposerOutput.readinessScore}% · {forfyAiComposerOutput.readinessLabel}</span>
            </div>

            <p>{forfyAiComposerOutput.customerPromise}</p>

            <ul>
              {(forfyAiComposerOutput.missingFields.length ? forfyAiComposerOutput.missingFields : ['Validation finale humaine']).slice(0, 4).map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        )}

        {forfyAiComposerOutput && (
          <div
            className="forfy-commerce-ai-dossier-actions"
            data-forfy-ai-dossier-actions="v22o5"
            aria-label="Actions du dossier Forfy IA"
          >
            <div className="forfy-commerce-ai-dossier-actions__header">
              <div>
                <p className="forfy-commerce-cockpit__card-eyebrow">Actions Forfy IA</p>
                <h5>Transformer le dossier en action</h5>
                <span>
                  Applique le meilleur du dossier, prépare l’offre et garde la validation humaine avant publication.
                </span>
              </div>

              <strong>
                {forfyAiDossierActionStatus === 'prepared'
                  ? 'Préparé'
                  : forfyAiDossierActionStatus === 'applied'
                    ? 'Appliqué'
                    : forfyAiDossierActionStatus === 'copied'
                      ? 'Copié'
                      : 'À valider'}
              </strong>
            </div>

            <div className="forfy-commerce-ai-dossier-actions__grid">
              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleApplyForfyAiDossier}>
                Appliquer titre et promesse
              </button>

              <button type="button" className="forfy-commerce-cockpit__primary" onClick={handlePrepareForfyAiDossierInOffers}>
                Préparer dans Offres
              </button>

              <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopyForfyAiFullDossier}>
                Copier dossier complet
              </button>
            </div>

            <p>
              Aucune publication automatique. Le dossier est préparé pour le flux Offres, puis validé par un humain.
            </p>
          </div>
        )}

        <div className="forfy-commerce-ai-composer__guardrail">
          Forfy IA prépare seulement un brouillon. Validation humaine obligatoire. Giftshop reste l’autorité financière;
          Reserve reste l’autorité de disponibilité lorsque requis.
        </div>
      </section>

      <div className="forfy-commerce-offer-assistant__actions">
        <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleOpenOffers}>
          <FaExternalLinkAlt /> Ouvrir Offres
        </button>

        <button type="button" className="forfy-commerce-cockpit__secondary" onClick={handleCopy}>
          <FaClipboard /> {copied ? 'Copié' : 'Copier la fiche'}
        </button>

        <button type="button" className="forfy-commerce-cockpit__primary" onClick={handleSendToAssistant}>
          <FaPaperPlane /> Envoyer à l’assistant
        </button>

        <button type="button" className="forfy-commerce-cockpit__secondary" onClick={() => setDraft(defaultOfferDraft)}>
          <FaPlusCircle /> Réinitialiser
        </button>
      </div>
    </section>
  );
};

export default ForfyCommerceOfferAssistant;
